import { createEffect } from 'effector';
import { DateTime } from 'luxon';

import {
  EntInvoicePaymentDetails,
  EntInvoicePaymentsDetails,
  InvoiceLineItemType,
} from '@m/types/api';
import { GrpcCurrency } from '@m/types/grpc/GrpcTypes';
import { apiFetch } from '@m/utils/http';

/**
 * Get default billing method for `enterpriseId`
 * Will return `null` if no billing method set.
 */
export const getPaymentMethod = createEffect(({ enterpriseId }: { enterpriseId: number }) =>
  apiFetch(`/admin/enterprise/${enterpriseId}/payment-methods/default`),
);

export const getAllPaymentMethods = createEffect(({ enterpriseId }: { enterpriseId: number }) =>
  apiFetch(`/admin/enterprise/${enterpriseId}/payment-methods`),
);

export const createNewACHPaymentMethod = createEffect(
  ({ enterpriseId }: { enterpriseId: number }) =>
    apiFetch(`/admin/enterprise/${enterpriseId}/payment-methods/stripe-ach`, {
      method: 'POST',
    }),
);

export const deletePaymentMethod = createEffect(
  ({ enterpriseId, paymentMethodId }: { enterpriseId: number; paymentMethodId: number }) =>
    apiFetch(`/admin/enterprise/${enterpriseId}/payment-method/${paymentMethodId}`, {
      method: 'DELETE',
    }),
);

// charge invoice
export const chargeInvoiceServiceInvoice = ({
  body,
}: {
  body: {
    chargeAmount?: GrpcCurrency;
    invoice_id: string;
    source: {
      customer_default: {
        enterprise_id: string;
      };
    };
  };
}) =>
  apiFetch('/v2/finance-operations/invoices/charge', {
    method: 'POST',
    body,
  });

// Make invoice editable by creating a copy and incrementing the invoice number
export const makeInvoiceEditable = ({ invoiceId }: { invoiceId: string }) =>
  apiFetch(`/v2/finance-operations/invoices/${invoiceId}/replace`, {
    method: 'POST',
    body: {
      invoiceId,
    },
  });

// update invoice status to paid (different from other status)
export const updateInvoiceServiceInvoiceStatusToPaid = ({
  invoiceId,
  body,
}: {
  body: {
    invoice_id: string;
    note?: string;
  };
  invoiceId: string;
}) =>
  apiFetch(`/v2/finance-operations/invoices/${invoiceId}/mark-as-paid`, {
    method: 'POST',
    body,
  });

// update invoice status
export const updateInvoiceServiceInvoiceStatus = ({
  invoiceId,
  body,
}: {
  body: {
    invoice_id: string;
    note?: string;
    status: string;
  };
  invoiceId: string;
}) =>
  apiFetch(`/v2/finance-operations/invoices/${invoiceId}/status`, {
    method: 'PUT',
    body,
  });

// retreive an invoice by uuid
export const getInvoiceServiceInvoice = (uuid: string, includeAdditionalInfo?: boolean) =>
  apiFetch(
    `/v2/finance-operations/invoices/${uuid}${includeAdditionalInfo ? '?includeAdditionalInfo=true' : ''}`,
  );

export const getAllInvoiceServiceInvoice = ({
  entId,
  fromInvoiceDate,
  toInvoiceDate,
  invoiceStatus,
}: {
  entId: number;
  fromInvoiceDate?: DateTime;
  invoiceStatus?: string;
  toInvoiceDate?: DateTime;
}) =>
  apiFetch('/v2/finance-operations/invoices', {
    method: 'POST',
    body: {
      customer: {
        enterpriseId: entId,
      },
      fromInvoiceDate,
      toInvoiceDate,
      invoiceStatus,
    },
  });

export const getInvoicePayments = (uuid: string) =>
  apiFetch(`/v2/finance-operations/history/invoices/${uuid}`);

export const getInvoicePaymentStatus = (uuid: string) =>
  apiFetch<{ paymentDetails: EntInvoicePaymentDetails; paymentUuid: string }>(
    `/v2/finance-operations/payments/${uuid}`,
  );

export const getInvoicePaymentsStatus = (uuid: string) =>
  apiFetch<{ payments: EntInvoicePaymentsDetails[] }>(
    `/v2/finance-operations/payments?paymentIds=${uuid}`,
  );

export const calculateNewInvoice = (body: {
  customer: {
    enterprise_id: number;
  };
  line_items: Partial<InvoiceLineItemType>[];
}) =>
  apiFetch('/v2/finance-operations/invoices/calculate', {
    method: 'POST',
    body,
  });

export const upsertNewLineItems = (data: {
  invoice_id: string;
  line_items: InvoiceLineItemType[];
  reason_for_upsert?: string;
}) =>
  apiFetch(`/v2/finance-operations/invoices/${data.invoice_id}/upsert-line-items`, {
    method: 'POST',
    body: {
      invoice_id: data.invoice_id,
      line_items: data.line_items,
      reason_for_upsert: data.reason_for_upsert,
    },
  });

export const createInvoice = (data: {
  customer: {
    enterprise_id: number;
  };
  invoice_date: string;
  invoice_frequency: string;
  invoice_id: string;
  line_items: InvoiceLineItemType[];
}) =>
  apiFetch('/v2/finance-operations/invoices/lineItems', {
    method: 'POST',
    body: {
      invoice_id: data.invoice_id,
      line_items: data.line_items,
      invoice_frequency: data.invoice_frequency,
      invoice_date: data.invoice_date,
      customer: data.customer,
      includeAdditionalInfo: false,
    },
  });

export const getInvoicePreview = (enterpriseId: number) =>
  apiFetch('/v2/finance-operations/invoices/upcoming-charges', {
    method: 'GET',
    params: { enterpriseId },
  });
